import { useState, useEffect } from 'react'
import { useCategories, useContentAccess, useCourses } from '../../hooks/useApi'
import FilterModal from './FilterModal'
import './SectionTabs.css'
import { SlidersHorizontal } from 'lucide-react'

interface SectionTabsProps {
  activeSection: string
  onSectionChange: (section: string) => void
  activeSubsection?: string
  onSubsectionChange?: (subsection: string) => void
  activeSubSubsection?: string
  onSubSubsectionChange?: (subSubsection: string) => void
  onFirstTabStatusChange?: (isFirstTab: boolean) => void
}

const SectionTabs = ({
  activeSection,
  onSectionChange,
  activeSubsection,
  onSubsectionChange,
  activeSubSubsection,
  onSubSubsectionChange,
  onFirstTabStatusChange,
}: SectionTabsProps) => {
  // ВАЖНО: Все хуки должны быть вызваны до любых условных возвратов
  const { hasSubscription } = useContentAccess()
  const { data: categories, isLoading } = useCategories(hasSubscription)
  const { data: coursesData } = useCourses()
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)

  // Проверяем, есть ли курсы
  const hasCourses = (coursesData?.courses?.length || 0) > 0

  // Вычисляем количество активных уровней фильтрации
  const getActiveFiltersCount = () => {
    let count = 0
    if (activeSection) count++
    if (activeSubsection) count++
    if (activeSubSubsection) count++
    return count
  }

  const activeFiltersCount = getActiveFiltersCount()

  // Функция для подсчета общего количества карточек в категории и всех её подкатегориях
  const getTotalCardCount = (category: any): number => {
    let count = 0

    // Считаем карточки в самой категории
    if (category.card_ids && category.card_ids.length > 0) {
      count += category.card_ids.length
    }

    // Рекурсивно считаем карточки во всех подкатегориях
    if (category.subcategories && category.subcategories.length > 0) {
      category.subcategories.forEach((subcategory: any) => {
        count += getTotalCardCount(subcategory)
      })
    }

    return count
  }

  // Берем только категории level 1 (основные разделы) - только если есть данные
  const sections = categories?.categories
    ? categories.categories
        .filter(category => category.level === 1)
        .filter(category => getTotalCardCount(category) > 0) // Фильтруем категории с карточками
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(category => ({
          id: category.category_id.toString(),
          name: category.name,
        }))
    : []

  // Определяем, является ли активный таб первым
  const firstTabId = sections.length > 0 ? sections[0].id : null
  const isFirstTabActive = activeSection === firstTabId

  // Уведомляем родительский компонент о статусе первого таба
  useEffect(() => {
    onFirstTabStatusChange?.(isFirstTabActive)
  }, [isFirstTabActive, onFirstTabStatusChange])

  // ТЕПЕРЬ можно делать условные возвраты после всех хуков

  // Если нет подписки, не показываем табы вообще
  if (!hasSubscription) {
    return null
  }

  if (isLoading || !categories?.categories) {
    return (
      <div className="section-tabs">
        <div className="tabs-container">
          <div className="tab-skeleton skeleton-short"></div>
          <div className="tab-skeleton skeleton-medium"></div>
          <div className="tab-skeleton skeleton-long"></div>
          <div className="tab-skeleton skeleton-medium"></div>
          <div className="tab-skeleton skeleton-short"></div>
        </div>
      </div>
    )
  }

  // Функция поиска категории по ID в дереве
  const findCategoryById = (cats: any[], id: string): any => {
    for (const cat of cats) {
      if (cat.category_id.toString() === id) return cat
      if (cat.subcategories) {
        const found = findCategoryById(cat.subcategories, id)
        if (found) return found
      }
    }
    return null
  }

  // Находим выбранную секцию для получения подкатегорий
  let sectionIdToUse = activeSection

  // Если активна подподкатегория, найдем секцию через родителей
  if (activeSubSubsection && categories?.categories) {
    const level3Cat = findCategoryById(
      categories.categories,
      activeSubSubsection
    )
    if (level3Cat && level3Cat.parent_category_id) {
      const level2Cat = findCategoryById(
        categories.categories,
        level3Cat.parent_category_id.toString()
      )
      if (level2Cat && level2Cat.parent_category_id) {
        sectionIdToUse = level2Cat.parent_category_id.toString()
      }
    }
  }

  const selectedSection = sectionIdToUse
    ? categories.categories.find(
        cat => cat.category_id.toString() === sectionIdToUse && cat.level === 1
      )
    : null

  // Определяем активную подсекцию (может быть задана напрямую или вычислена из level 3)
  let activeSubsectionToUse = activeSubsection

  if (activeSubSubsection && categories?.categories) {
    const level3Cat = findCategoryById(
      categories.categories,
      activeSubSubsection
    )
    if (level3Cat && level3Cat.parent_category_id) {
      activeSubsectionToUse = level3Cat.parent_category_id.toString()
    }
  }

  // Получаем подкатегории level 2 выбранной секции
  const allSubsections =
    selectedSection && 'subcategories' in selectedSection
      ? selectedSection.subcategories
          .filter(subcat => getTotalCardCount(subcat) > 0) // Фильтруем подкатегории с карточками
          .map(subcat => ({
            id: subcat.category_id.toString(),
            name: subcat.name,
          }))
      : []

  // Показываем только первые 3 подкатегории
  const visibleSubsections = allSubsections.slice(0, 3)
  const hasMoreSubsections = allSubsections.length > 3

  // Toggle логика для секций
  const handleSectionClick = (sectionId: string) => {
    if (activeSection === sectionId) {
      // Если кликаем на уже активную секцию - сбрасываем выбор
      onSectionChange('')
      onSubsectionChange?.('')
      onSubSubsectionChange?.('')
    } else {
      // Если кликаем на новую секцию - выбираем её
      onSectionChange(sectionId)
      onSubsectionChange?.('')
      onSubSubsectionChange?.('')
    }
  }

  const handleMoreClick = () => {
    setIsFilterModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsFilterModalOpen(false)
  }

  // Обработчик применения фильтра из модального окна
  const handleFilterApply = (
    sectionId: string,
    subsectionId: string,
    subSubsectionId?: string
  ) => {
    if (sectionId) {
      onSectionChange(sectionId)
    }
    if (subsectionId) {
      onSubsectionChange?.(subsectionId)
    } else {
      onSubsectionChange?.('')
    }
    if (subSubsectionId) {
      onSubSubsectionChange?.(subSubsectionId)
    } else {
      onSubSubsectionChange?.('')
    }
  }

  // Преобразуем структуру данных для FilterModal
  const modalCategories = sections.map(section => {
    const fullSection = categories.categories.find(
      cat => cat.category_id.toString() === section.id && cat.level === 1
    )

    const hasSubcategories = fullSection && 'subcategories' in fullSection

    return {
      id: section.id,
      name: section.name,
      card_ids: (fullSection as any)?.card_ids || [],
      subcategories: hasSubcategories
        ? fullSection.subcategories?.map((subcat: any) => ({
            id: subcat.category_id.toString(),
            name: subcat.name,
            card_ids: subcat.card_ids || [],
            subcategories: subcat.subcategories?.map((subsubcat: any) => ({
              id: subsubcat.category_id.toString(),
              name: subsubcat.name,
              card_ids: subsubcat.card_ids || [],
            })),
          }))
        : undefined,
    }
  })

  return (
    <>
      <div className="section-tabs">
        <div className="tabs-container">
          {sections.map(section => (
            <button
              key={section.id}
              className={`tab ${sectionIdToUse === section.id ? 'active' : ''}`}
              onClick={() => handleSectionClick(section.id)}
            >
              {section.name}
            </button>
          ))}
          {/* Вкладка Курсы */}
          {hasCourses && (
            <button
              className={`tab ${activeSection === 'courses' ? 'active' : ''}`}
              onClick={() => handleSectionClick('courses')}
            >
              Курсы
            </button>
          )}
        </div>

        {/* Подразделы (только первые 3) */}
        {visibleSubsections.length > 0 && (
          <div className="tabs-container subsections">
            {visibleSubsections.map(subsection => (
              <button
                key={subsection.id}
                className={`tab ${activeSubsectionToUse === subsection.id ? 'active' : ''}`}
                onClick={() => onSubsectionChange?.(subsection.id)}
              >
                {subsection.name}
              </button>
            ))}
            {hasMoreSubsections && (
              <button className="tab more-button" onClick={handleMoreClick}>
                <SlidersHorizontal className="tab-icon" size={12} />
                Больше
                {activeFiltersCount > 0 && (
                  <span className="filter-badge">{activeFiltersCount}</span>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={handleCloseModal}
        categories={modalCategories}
        activeSection={sectionIdToUse}
        activeSubsection={activeSubsectionToUse}
        activeSubSubsection={activeSubSubsection}
        onFilterApply={handleFilterApply}
      />
    </>
  )
}

export default SectionTabs
